import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Facebook, Instagram, Music2, Heart, Scissors, BedDouble, ShoppingBag, Clock } from "lucide-react";
import { Button } from "../../components/ui/button";

const CONTACT = {
  address: "2nd Floor Plaza Deal Rosa Bldg., Mañalac Avenue, North Dang-Hari, Taguig City 1630",
  phone: "+63 997 230 0944",
  phoneHref: "tel:+639972300944",
};

const SOCIALS = [
  { name: "Facebook", handle: "Pet's Pantry", href: "https://www.facebook.com/petspantry", icon: Facebook, color: "bg-[#1877F2]" },
  { name: "TikTok", handle: "Pet's Pantry", href: "https://www.tiktok.com/@petspantry", icon: Music2, color: "bg-slate-900" },
  { name: "Instagram", handle: "@petspantry", href: "https://www.instagram.com/petspantry", icon: Instagram, color: "bg-[#E84855]" },
];

const VALUES = [
  { icon: ShoppingBag, title: "Quality Supplies", text: "Hand-picked kibble, treats and essentials we would give our own pets — always fresh, always fairly priced." },
  { icon: Scissors, title: "Gentle Grooming", text: "Certified groomers who treat every pet like family, from quick nail trims to full spa days." },
  { icon: BedDouble, title: "Safe Boarding", text: "Cozy, supervised suites with daily feeding, walks and activity logs so you never miss a moment." },
];

export default function About() {
  const navigate = useNavigate();
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center animate-rise">
        <img src="/logo-wide.png" alt="Pet's Pantry — Supplies & Grooming" className="w-full max-w-lg mx-auto rounded-[2rem] shadow-lg" data-testid="about-logo" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-8 text-slate-900">
          About <span className="text-[#0E7C84]">Pet's Pantry</span>
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          We started Pet's Pantry with one simple belief: every pet deserves to be pampered, and every
          pet parent deserves convenience. From our shelves stocked with quality supplies to our grooming
          studio and cozy boarding suites, everything we do is built around your pet's happiness —
          and your peace of mind.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-12">
        {VALUES.map((v, i) => (
          <div key={v.title} className="bg-white rounded-2xl border border-[#BFE8E9] p-6 hover-lift animate-rise" style={{ animationDelay: `${i * 90}ms` }} data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="inline-flex w-10 h-10 rounded-xl bg-[#0E7C84] items-center justify-center mb-3">
              <v.icon className="w-5 h-5 text-white" />
            </span>
            <h3 className="font-display font-semibold text-lg">{v.title}</h3>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{v.text}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-12">
        <div className="bg-[#093F46] text-white rounded-2xl p-8 animate-rise">
          <h2 className="font-display font-bold text-xl mb-5 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#5ce1e6]" /> Visit our store
          </h2>
          <p className="text-emerald-50/90 leading-relaxed" data-testid="about-address">{CONTACT.address}</p>
          <div className="mt-5 space-y-3 text-sm">
            <a href={CONTACT.phoneHref} className="flex items-center gap-3 hover:text-[#5ce1e6] transition-colors" data-testid="about-phone">
              <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Phone className="w-4 h-4" /></span>
              {CONTACT.phone}
            </a>
            <p className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Clock className="w-4 h-4" /></span>
              Open daily, 9:00 AM – 6:00 PM
            </p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-8 animate-rise" style={{ animationDelay: "90ms" }}>
          <h2 className="font-display font-bold text-xl mb-5 flex items-center gap-2 text-slate-900">
            <Heart className="w-5 h-5 text-[#E84855]" /> Follow the pack
          </h2>
          <div className="space-y-3">
            {SOCIALS.map((s) => (
              <a key={s.name} href={s.href} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 border border-[#BFE8E9] rounded-xl p-3 hover:border-[#0E7C84] transition-colors group"
                data-testid={`social-${s.name.toLowerCase()}`}>
                <span className={`w-10 h-10 rounded-xl ${s.color} flex items-center justify-center shrink-0`}>
                  <s.icon className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="font-semibold text-sm group-hover:text-[#0E7C84] transition-colors">{s.name}</p>
                  <p className="text-xs text-slate-500">{s.handle}</p>
                </div>
              </a>
            ))}
          </div>
          <Button className="w-full mt-6 bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => navigate("/book")} data-testid="about-book-btn">
            Book a visit
          </Button>
        </div>
      </div>
    </div>
  );
}
